import React, {
  PureComponent,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import AntdTable from 'antd/lib/table';
import AntdInput from 'antd/lib/input';
import AmtdCheckbox from 'antd/lib/checkbox';
import AntdButton from 'antd/lib/button';
import {
  DeleteOutlined,
} from '@ant-design/icons';

import Admin from 'hive-admin/src/components/Admin';
import Field from 'hive-admin/src/components/Field';

// Debounce hook
function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
}

// Wrapper to make any input debounced
function withDebounce(InputComponent, delay = 300) {
  return function DebouncedInput({
    value: externalValue,
    onChange,
    ...props
  }) {
    const [localValue, setLocalValue] = useState(externalValue);
    const lastExternalValue = useRef(externalValue);

    // Sync with external value when it changes (e.g., from server)
    useEffect(() => {
      if (externalValue !== lastExternalValue.current) {
        setLocalValue(externalValue);
        lastExternalValue.current = externalValue;
      }
    }, [externalValue]);

    const debouncedOnChange = useDebouncedCallback(onChange, delay);

    const handleChange = useCallback((e) => {
      const newValue = e?.target?.value ?? e;
      setLocalValue(newValue);
      // Create a synthetic event for debounced call
      if (e?.target) {
        debouncedOnChange(e);
      } else {
        debouncedOnChange(newValue);
      }
    }, [debouncedOnChange]);

    return (
      <InputComponent {...props} value={localValue} onChange={handleChange} />
    );
  };
}

export const InputWrapper = styled.div`
  ${props => props.minWidth && css`min-width: ${props.minWidth};`}
  ${props => props.maxWidth && css`max-width: ${props.maxWidth};`}
  width: 100%;
`;

const InputAlignedBase = styled(AntdInput)`
  text-align: ${({ align }) => align || 'left'};
  width: 100%;
  border-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
`;

export const InputAligned = withDebounce(InputAlignedBase);

const InputTextAreaBase = styled(AntdInput.TextArea)`
  text-align: ${({ align }) => align || 'left'};
  width: 100%;
  border-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  resize: none !important;
  padding: 0 !important;
  min-height: auto !important;
  line-height: 1.4 !important;
`;

const InputTextAreaWrapping = (props) => (
  <InputTextAreaBase
    {...props}
    autoSize={{ minRows: 1, maxRows: 4 }}
  />
);

export const InputWrapping = withDebounce(InputTextAreaWrapping);

export const InputCheckbox = ({ value, ...props }) => (
  <AmtdCheckbox
    {...props}
    checked={!!value}
    data-input-value-uses-checked="true"
  />
);

export const InputFormField = ({ fieldName, fieldConfig, ...props }) => {
  const field = useMemo(
    () => Admin.compileFromLibrary([fieldName, fieldConfig]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return field.render({ ...props });
};

const LabelWrapper = styled.div`
  white-space: nowrap;

  strong {
    font-weight: 400;
  }
`;

const RowNumber = styled.span`
  color: #999;
  font-size: 12px;
`;

const DeleteButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;

  &:hover {
    background: #fff1f0;
    color: #ff4d4f;
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 8px;
`;

const TableWrapper = styled.div`
  .ant-table {
    font-size: 13px;
  }
  .ant-table-thead > tr > th {
    background: #fafafa;
    padding: 8px 6px;
    font-weight: 500;
    font-size: 12px;
    color: #666;
    white-space: normal;
    word-break: break-word;
  }
  .ant-table-tbody > tr > td {
    padding: 6px 6px;
    white-space: normal;
    word-break: break-word;
    vertical-align: top;
  }
  .ant-table-tbody > tr:hover > td {
    background: #e6f7ff;
  }
  .ant-select-selection-item {
    white-space: normal !important;
    height: auto !important;
    line-height: 1.4 !important;
  }
`;

export class ColumnValue extends PureComponent {
  static getFieldProps({ props = {} }) {
    return props;
  }

  static getColumnLabel({ label }) {
    return label;
  }

  render() {
    const {
      fields,
      render,
      value = {},
      rowIndex,
      onChange,
      getLabel = this.constructor.getColumnLabel,
    } = this.props;
    return render
    ? render(value, this.props)
    : fields
    ? fields.map((field) => {
        const {
          name,
          maxWidth,
          minWidth,
          ComponentWrapper = InputWrapper,
          ComponentInput = InputAligned,
          getProps = this.constructor.getFieldProps,
          render: renderField,
        } = field;
        const inputProps = {
          value: value[name],
          'data-input-row': rowIndex,
          'data-input-name': name,
          onChange,
          disabled: this.props.disabled,
        };
        Object.assign(
          inputProps,
          getProps(field, value, this.props, inputProps),
        );
        return (
          <ComponentWrapper key={name} maxWidth={maxWidth} minWidth={minWidth}>
            {
                renderField
              ? renderField(inputProps, this.props)
              : (
                  <ComponentInput
                    {...inputProps}
                  />
                )
            }
          </ComponentWrapper>
        );
      })
    : (
        <LabelWrapper>
          {getLabel(value, this.props)}
        </LabelWrapper>
      );
  }
}

export class Table extends PureComponent {
  handleRowRemove = (rowIndex) => {
    this.props.onChange(this.props.value.reduce(
      (agr, row, index) => {
        if (index !== rowIndex) {
          agr.push(row);
        }
        return agr;
      },
      [],
    ));
  }

  handleChange = (event) => {
    const { nativeEvent, target } = event;
    const { value, checked } = target;
    const {
      inputName,
      inputRow: inputRowString,
      inputValueUsesChecked,
    } = (nativeEvent.target.dataset || {});
    if (inputName) {
      const inputRow = parseInt(inputRowString, 10);
      this.props.onChange(this.props.value.map((row, index) => {
        if (index === inputRow) {
          return {
            ...row,
            [inputName]: inputValueUsesChecked ? checked : value,
          };
        }
        return row;
      }));
    }
  }

  handleFormChange = (rowIndex, name, value) => {
    this.props.onChange(this.props.value.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...row,
          [name]: value,
        };
      }
      return row;
    }));
  }

  handleAddRow = () => {
    const newItem = this.props.controlsGetNewItem(
      (this.props.value || []).length,
      null,
      this.props,
    );
    this.props.onChange([...(this.props.value || []), newItem]);
  }

  renderRowNumber = (text, row, rowIndex) => (
    <RowNumber>{rowIndex + 1}</RowNumber>
  )

  renderDeleteButton = (text, row, rowIndex) => (
    this.props.disabled ? null : (
      <DeleteButton onClick={() => this.handleRowRemove(rowIndex)}>
        <DeleteOutlined />
      </DeleteButton>
    )
  )

  renderColumnValue(
    text,
    row = {},
    rowIndex,
    columnIndex,
    render,
    fields,
    label,
    getLabel,
  ) {
    return (
      <ColumnValue
        value={row}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        render={render}
        fields={fields}
        label={label}
        getLabel={getLabel}
        onChange={this.handleChange}
        onFormChange={this.handleFormChange}
        disabled={this.props.disabled}
        form={this.props.form}
        fieldProps={this.props.fieldProps}
      />
    );
  }

  render() {
    const {
      columns = [],
      value,
      controlsEnabled,
      controlsGetNewItem,
      table = {},
      disabled,
      ...props
    } = this.props;
    return (
      <TableWrapper>
        <AntdTable
          dataSource={value}
          size="small"
          rowKey="_id"
          showHeader={false}
          pagination={false}
          bordered
          {...props}
          {...table}
        >
          {controlsEnabled && (
            <AntdTable.Column
              key="row-number"
              title="#"
              align="center"
              render={this.renderRowNumber}
              width="35px"
            />
          )}
          {controlsEnabled && (
            <AntdTable.Column
              key="delete-column"
              title=""
              align="center"
              render={this.renderDeleteButton}
              width="35px"
            />
          )}
          {columns.map(({
            key,
            fields,
            label,
            getLabel,
            render,
            ...column
          }, index) => (
            <AntdTable.Column
              key={key || index}
              {...column}
              render={(...args) => this.renderColumnValue(
                ...args,
                index,
                render,
                fields,
                label,
                getLabel,
              )}
            />
          ))}
        </AntdTable>
        {controlsEnabled && !disabled && (
          <AddButtonWrapper>
            <AntdButton
              type="primary"
              ghost
              size="small"
              onClick={this.handleAddRow}
              icon="plus"
            >
              Dodaj
            </AntdButton>
          </AddButtonWrapper>
        )}
      </TableWrapper>
    );
  }
}

export default class FieldTable extends Field {
  static config = {
    ...Field.config,
    controlsEnabled: false,
    controlsGetNewItem: (/* index, props */) => ({}),
    getColumns: (props) => props.columns,
  }

  renderInput(props) {
    return (
      <Table
        {...props}
        columns={this.props.getColumns(this.props)}
        controlsEnabled={this.props.controlsEnabled}
        controlsGetNewItem={this.props.controlsGetNewItem}
        {...(this.props.table || {})}
        form={this.props.form}
        fieldProps={this.props}
      />
    );
  }
}

Admin.addToLibrary(
  'FieldTable',
  config => FieldTable.create(config),
);
